import axios from 'axios';
import config from '../config';

export interface EnrichmentReview {
  reviewId: number;
  productId: number;
  jobId: number | null;
  reason: string;
  severity: 'low' | 'medium' | 'high';
  status: 'open' | 'approved' | 'rejected' | 'resolved';
  createdAt: string;
  updatedAt: string;
  productNumber: string;
  brandName: string | null;
  publishDate: string | null;
  productName: string | null;
  productExists?: number;
  hasValidDescription: number;
  source: string | null;
  triggerType: string | null;
  jobStatus: string | null;
  decisionCount: number;
  openReviewCount: number;
  suggestedAction: unknown;
}

export interface EnrichmentDecision {
  decisionId: number;
  jobId: number;
  productId: number;
  decisionType: string;
  level: number;
  confidence: number;
  status: 'staged' | 'applied' | 'needs_review' | 'rejected';
  rationale: string | null;
  input: unknown;
  output: unknown;
}

export interface EnrichmentReviewDetail {
  review: EnrichmentReview & {
    longDescription?: string | null;
    lastError?: string | null;
  };
  decisions: EnrichmentDecision[];
  media: Array<{ attribute1Number: string; imageUrl: string }>;
  stock: Array<{
    attribute1Code: string;
    attribute1Number: string;
    availableBalance: number;
  }>;
  snapshots: Array<{
    snapshotId: number;
    attribute1Number: string | null;
    ean: string | null;
    fetchedAt: string;
    apiStatus: string;
    sourceHash: string;
  }>;
}

export interface EnrichmentReviewsResponse {
  reviews: EnrichmentReview[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  counts: {
    open: number;
    approved: number;
    rejected: number;
    resolved: number;
    highOpen: number;
  };
}

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export async function fetchEnrichmentReviews(params: {
  page?: number;
  limit?: number;
  status?: string;
  reason?: string;
  search?: string;
}): Promise<EnrichmentReviewsResponse> {
  const response = await axios.get(`${config.API_URL}/enrichment/reviews`, {
    params,
    headers: authHeaders(),
  });
  return response.data;
}

export async function fetchEnrichmentReviewDetail(
  reviewId: number,
): Promise<EnrichmentReviewDetail> {
  const response = await axios.get(
    `${config.API_URL}/enrichment/reviews/${reviewId}`,
    { headers: authHeaders() },
  );
  return response.data;
}

export async function approveEnrichmentReview(reviewId: number): Promise<void> {
  await axios.post(
    `${config.API_URL}/enrichment/reviews/${reviewId}/approve`,
    {},
    { headers: authHeaders() },
  );
}

export async function rejectEnrichmentReview(reviewId: number): Promise<void> {
  await axios.post(
    `${config.API_URL}/enrichment/reviews/${reviewId}/reject`,
    {},
    { headers: authHeaders() },
  );
}

export async function applyProductEnrichment(productId: number): Promise<{
  applied: string[];
  skipped: string[];
}> {
  const response = await axios.post(
    `${config.API_URL}/enrichment/products/${productId}/apply`,
    {},
    { headers: authHeaders() },
  );
  return response.data;
}

export async function runBestsellerEnrichment(productNumber: string): Promise<{
  jobs: Array<{ productId: number; jobId: number }>;
  missingProductNumbers?: string[];
}> {
  const response = await axios.post(
    `${config.API_URL}/enrichment/bestseller/jobs/run`,
    { productNumber },
    { headers: authHeaders() },
  );
  return response.data;
}
